import {
  CreateEventFormAnswersForm,
  createEventFormAnswersSchema,
  defaultCreateEventFormAnswersValues,
  useCreateEventFormAnswersByOrderMutation,
} from "@ac-expo/api";
import { EventFormQuestionOnEvent } from "@ac-expo/api-types";
import { EventForm, Form, toast } from "@ac-expo/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { EmbedNavigation } from "./EmbedNavigation";
import { STEPS } from "./EmbedTicketing";

type EmbedFormComponentProps = {
  setStep: (step: STEPS) => void;
  eventId: string;
  isTransactionComplete?: boolean;
  eventFormQuestionsOnEvent: EventFormQuestionOnEvent[] | undefined;
  formAnswersToken: string | null;
  formCount: number;
};

export function EmbedFormComponent({
  setStep,
  eventId,
  isTransactionComplete = false,
  eventFormQuestionsOnEvent,
  formAnswersToken,
  formCount,
}: EmbedFormComponentProps) {
  const { mutateAsync: createEventFormAnswersByOrder, isPending } =
    useCreateEventFormAnswersByOrderMutation();

  // One form per ticket in the basket, each participant answering for their own
  const totalForms = Math.max(formCount, 1);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [answersByForm, setAnswersByForm] = useState<CreateEventFormAnswersForm[]>([]);
  const isLastForm = currentFormIndex === totalForms - 1;

  const blankAnswers = useMemo(
    () =>
      (eventFormQuestionsOnEvent ?? []).map((questionOnEvent) => ({
        questionId: questionOnEvent.question.id,
        answer: "",
        answerList: [],
      })),
    [eventFormQuestionsOnEvent]
  );

  const form = useForm<CreateEventFormAnswersForm>({
    resolver: zodResolver(createEventFormAnswersSchema),
    defaultValues: {
      ...defaultCreateEventFormAnswersValues,
      eventId,
      answers: blankAnswers,
    },
  });

  const { control, handleSubmit, formState, reset } = form;

  useEffect(() => {
    reset({
      ...defaultCreateEventFormAnswersValues,
      eventId,
      answers: answersByForm[currentFormIndex]?.answers ?? blankAnswers,
    });
  }, [answersByForm, blankAnswers, currentFormIndex, eventId, reset]);

  const submitAllForms = async (forms: CreateEventFormAnswersForm[]) => {
    if (!formAnswersToken) {
      toast({
        title: "Erreur lors de la soumission du formulaire",
        variant: "important",
      });
      return;
    }
    try {
      // Sequential: each submission syncs the buyer's event interests, so
      // parallel calls would race on the same rows
      for (const data of forms) {
        await createEventFormAnswersByOrder({ data, orderToken: formAnswersToken });
      }
      toast({
        title: "Formulaire rempli avec succès",
      });
      setStep(isTransactionComplete ? "CONFIRMATION" : "PAYMENT");
    } catch {
      toast({
        title: "Erreur lors de la soumission du formulaire",
        variant: "important",
      });
    }
  };

  const onSubmit = (data: CreateEventFormAnswersForm) => {
    const nextAnswersByForm = [...answersByForm];
    nextAnswersByForm[currentFormIndex] = data;
    setAnswersByForm(nextAnswersByForm);

    if (isLastForm) {
      void submitAllForms(nextAnswersByForm);
      return;
    }
    setCurrentFormIndex((index) => index + 1);
  };

  const handlePrevious = () => {
    if (currentFormIndex > 0) {
      setCurrentFormIndex((index) => index - 1);
      return;
    }
    setStep("INFORMATIONS");
  };

  if (!eventFormQuestionsOnEvent || eventFormQuestionsOnEvent.length === 0) {
    return (
      <div className="space-y-4">
        <p>Aucun formulaire requis pour cet événement.</p>
        <EmbedNavigation
          setPreviousStep={() => setStep("INFORMATIONS")}
          setNextStep={() =>
            setStep(isTransactionComplete ? "CONFIRMATION" : "PAYMENT")
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Formulaire d&apos;inscription</h3>
      {totalForms > 1 && (
        <p className="text-sm text-gray-600">
          Billet {currentFormIndex + 1} sur {totalForms}
        </p>
      )}
      <p className="text-gray-600">
        {totalForms > 1
          ? "Veuillez remplir un formulaire par billet pour finaliser votre panier."
          : "Veuillez remplir ce formulaire pour finaliser votre panier."}
      </p>

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <EventForm
            eventFormQuestionsOnEvent={eventFormQuestionsOnEvent}
            control={control}
            isReadOnly={false}
          />

          <EmbedNavigation
            setPreviousStep={handlePrevious}
            setNextStep={handleSubmit(onSubmit)}
            isNextStepDisabled={isPending || !formState.isValid || !formAnswersToken}
            nextStepLabel={isLastForm ? "Suivant" : "Billet suivant"}
          />
        </form>
      </Form>
    </div>
  );
}
